import { useQuery } from "@apollo/client";
import { Button, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement } from "react";

import { ListTollFreeRegistrationsQuery } from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  DELETE_TOLL_FREE_REGISTRATION,
  LIST_TOLL_FREE_REGISTRATIONS,
} from "~frontend/graphql/tollFreeRegistrations";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

const PendingTollFreeRegistrationBanner = ({
  organizationName,
  createdAt,
  createdBy,
  onDelete,
}: {
  organizationName: string;
  createdAt: string;
  createdBy?: { fullName: string };
  onDelete: () => void;
}): ReactElement => {
  const confirm = useConfirm();
  const isStaff = useCurrentUserIsStaff();

  const handleDelete = async () => {
    const { confirmed } = await confirm({
      title: "Delete Toll-Free Registration",
      description: `Are you sure you want to delete the toll-free registration for ${organizationName}?`,
    });
    if (confirmed) {
      onDelete();
    }
  };

  return (
    <FlexBox
      sx={{
        p: 3,
        color: palette.navy,
        borderRadius: 0.5,
        backgroundColor: palette.lightOrange,
        mb: 2,
      }}
      justifyContent="space-between"
      alignItems="flex-start"
    >
      <FlexBox direction="column" gap={0.5}>
        <Typography variant="body2Bold" component="div">
          Toll-Free Submission for {organizationName}: Under Review
        </Typography>
        {createdAt ? (
          <Typography variant="body2" component="div">
            <strong>Submitted:</strong>{" "}
            {new Date(createdAt).toLocaleDateString()}{" "}
            {createdBy && `by ${createdBy.fullName}`}
          </Typography>
        ) : (
          <div />
        )}

        <Typography variant="body2" component="div">
          Your tollfree registration is under review. The external vendor
          typically needs 3-5 business days to approve registrations.
        </Typography>
        <Typography variant="body2" component="div">
          Questions? Reach out to{" "}
          <a href="mailto:compliance@scaletowin.com">
            compliance@scaletowin.com
          </a>
          .
        </Typography>
      </FlexBox>
      {isStaff && (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleDelete}
          sx={{
            ml: 2,
            flexShrink: 0,
          }}
        >
          <Typography variant="h4Caps" component="div">
            Delete
          </Typography>
        </Button>
      )}
    </FlexBox>
  );
};

export const InProgressTollFreeRegistrations = (): ReactElement => {
  const { subaccount } = useSubaccount()!;
  const {
    data: tfRegistrations,
    loading,
    error,
    refetch,
  } = useQuery<ListTollFreeRegistrationsQuery>(LIST_TOLL_FREE_REGISTRATIONS, {
    variables: {
      input: {
        subaccountId: subaccount!.id,
      },
    },
    skip: !subaccount,
  });

  const [deleteTollFreeRegistration] = useMutation(
    DELETE_TOLL_FREE_REGISTRATION,
    {
      onErrorSnackbarMessage: "Failed to delete toll-free registration",
    }
  );

  const handleDelete = async (registrationId: string) => {
    const result = await deleteTollFreeRegistration({
      variables: {
        input: { registrationId },
      },
    });

    if (!result.errors) {
      void refetch();
    }
  };

  return (
    <AsyncWrapper data={tfRegistrations} loading={loading} errors={error}>
      {({ listTollFreeRegistrations }) => {
        return listTollFreeRegistrations.map(registration => {
          if (registration.submissionStatus !== "VERIFIED") {
            return (
              <PendingTollFreeRegistrationBanner
                key={registration.id}
                organizationName={registration.organizationName}
                createdAt={registration.createdAt}
                createdBy={
                  registration.createdBy
                    ? { fullName: registration.createdBy.fullName }
                    : undefined
                }
                onDelete={() => handleDelete(registration.id)}
              />
            );
          }
          return null;
        });
      }}
    </AsyncWrapper>
  );
};
